'use client'

// import "swiper/swiper-bundle.css";
import React, { useEffect, useRef } from "react";
import Headline from "@/components/ui/Headline/Headline";
import DeviceTestimonialCard from "@/components/client/DeviceTestimonialCard/DeviceTestimonialCard";
import { LanguageType, LanguageDirection, BeautyDeviceSectionContent } from "@/lib/types";
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useGSAP } from "@gsap/react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Pagination, Autoplay, Mousewheel } from "swiper/modules";
import type { Swiper as SwiperType } from "swiper/types";
import '@/components/ui/TestimonialCard/TestimonialCard.css';
import PrimaryButton from "@/components/ui/PrimaryButton/PrimaryButton";
import { useIsLg, useIsTb } from '@/contexts/BreakpointContext';
gsap.registerPlugin(useGSAP, ScrollTrigger);

interface DevicesSectionProps {
  language?: LanguageType
  headlineText: string
  subheadingText: string
  beautyDevicesSectionPageData: BeautyDeviceSectionContent
  buttonText?: string
  onOpenSlidingForm?: () => void
  id?: string
}

const LANGUAGE_TO_DIRECTION: Record<LanguageType, LanguageDirection> = {
  english: "ltr",
  arabic: "rtl"
};

interface ImageCard {
  imageUrl: string;
  altText: string;
  title: string;
  description: string;
  logoUrl: string;
}


const LPDevicesSection: React.FC<DevicesSectionProps> = ({
  language = "english",
  beautyDevicesSectionPageData,
  headlineText,
  subheadingText,
  buttonText = "Join our waitlist",
  onOpenSlidingForm,
  id,
}) => {
  const isLg = useIsLg();
  const isTb = useIsTb();
  const swiperRef = useRef<SwiperType>(null);
  // const containerRef = useRef<HTMLDivElement>(null);

  const imageCards: ImageCard[] = beautyDevicesSectionPageData.beautyDevices.sort((a, b) => a.deviceOrder - b.deviceOrder).map((device) => ({
    imageUrl: device.deviceImage.url,
    altText: device.deviceImageAltText,
    title: device.deviceName,
    description: device.deviceDescription,
    logoUrl: device.deviceLogo.url
  }));


  // TODO: triggers are a bit off here - dont know why atm

  useGSAP(() => {
    // const container = containerRef.current;
    // if (container) {
    if (isLg) {
    const cards: HTMLElement[] = Array.from(document.querySelectorAll('.device-image-card'));

    cards.forEach(card => {
      gsap.fromTo(card, {
        yPercent: -10,
      }, {
        yPercent: 10,
        ease: "none",
        force3D: true,
        scrollTrigger: {
          trigger: card,
          start: "top bottom",
          end: "bottom top",
          scrub: true,
          invalidateOnRefresh: true,
          // markers: true,
          id: "devices-section-scroll-trigger"
        },
        onEnter: () => {
          card.style.willChange = "transform";
        },
        onLeave: () => {
          card.style.willChange = "auto";
        },
        onEnterBack: () => {
          card.style.willChange = "transform";
        },
        onLeaveBack: () => {
          card.style.willChange = "auto";
        }
      });
    });
  }
  // }, [] );
  }, { dependencies: [isLg], revertOnUpdate: true });
  // }, { dependencies: [], revertOnUpdate: true});

  useEffect(() => {
    if (swiperRef.current) {
      swiperRef.current.snapGrid = [...swiperRef.current.slidesGrid];
    }
    return () => {
      if (swiperRef.current) {
      }
    };
  }, []);

  return (
    // section has custom padding!
    <section
      id={id}
      style={{ overscrollBehaviorX: 'none' }}
      // overflow-hidden
     className="
      w-full flex flex-col items-center justify-center
      gap-[2.5rem] lg:gap-[6rem]
      padding-main-x-mobile
      padding-main-x-desktop
    ">
      <div
        style={{ direction: LANGUAGE_TO_DIRECTION[language] }}
        className="
          w-full container-x-large"
      >

        <div className="flex flex-row items-center justify-center w-full">
          {/* Figma Heading Component, Center, Size L at its full 997px width */}
          <Headline
            type="wide"
            alignment='center'
            heading={headlineText}
            // paragraph={paragraphText}
            subheading={subheadingText}
            language={language}
            shouldAnimate={false}
          />

        </div>
      </div>

      <div
        style={{ direction: LANGUAGE_TO_DIRECTION[language] }}
        className="w-full container-x-large overflow-visible">

        {/* Desktop Grid Layout */}
        {isLg && (
          <div
            // ref={containerRef}
            className="
              gap-y-[2rem] gap-x-[1rem]
              grid
              [grid-template-columns:repeat(auto-fit,minmax(min(100%,20rem),1fr))]
            ">
            {imageCards.map((card) => (
              <DeviceTestimonialCard
                key={card.title}
                imageUrl={card.imageUrl}
                imageAlt={card.title}
                title={card.title}
                description={card.description}
                language={language}
                imageSizes="(min-width: 992px) 25vw, 100vw"
                badgeSize={isLg ? 'desktop' : 'mobile'}
              />
            ))}
          </div>
        )}

        {/* Mobile Swiper Layout */}
        {!isLg && (
          <div className="w-full overflow-hidden">
            <Swiper
              style={{
                width: '100%',
                overflow: 'visible'
              }}
              modules={[Pagination, Autoplay, Mousewheel]}
              pagination={{
                clickable: true,
                el: '.swiper-custom-pagination',
                bulletClass: 'swiper-pagination-bullet',
                bulletActiveClass: 'swiper-pagination-bullet-active',
              }}
              loop={imageCards.length > 1}
              spaceBetween={isTb ? 16 : 12}
              slidesPerView={isTb ? 2 : 1}
              autoplay={{
                delay: 7000,
                disableOnInteraction: true,
              }}
              onBeforeInit={(swiper) => {
                swiperRef.current = swiper;
              }}
              centeredSlidesBounds={isTb ? false : true}
              centeredSlides={isTb ? false : true}
              observer={true}
              observeSlideChildren={true}
              resizeObserver={true}
              // mousewheel={{
              //   forceToAxis: true,
              //   sensitivity: 0.5,
              //   releaseOnEdges: true,
              // }}
              className="!overflow-visible testimonial-swiper pb-8"
            >
              <div className="w-full overflow-hidden">
                <div className="w-full">
                  <div className="flex w-full">
                    {imageCards.map((card) => (
                      <SwiperSlide
                        key={card.title}
                        style={{
                          width: '100%',
                        }}
                        className="cursor-grab"
                      >
                        <DeviceTestimonialCard
                          imageUrl={card.imageUrl}
                          imageAlt={card.title}
                          title={card.title}
                          description={card.description}
                          language={language}
                          imageSizes="(min-width: 768px) 50vw, 100vw"
                          badgeSize={isTb ? 'desktop' : 'mobile'}
                        />
                      </SwiperSlide>
                    ))}
                  </div>
                </div>
              </div>

              {imageCards.length > 1 && (
                <div className="swiper-custom-pagination mt-[2rem] lg:mt-[2.5rem] w-full flex justify-center gap-[0.625rem]" />
              )}
            </Swiper>
          </div>
        )}

        {/* CTA - 72px below the cards on desktop, 40px on mobile (8px margin,
            which keeps the CTA glow from being clipped, + 32px padding) */}
        <div className="w-full mt-[0.5rem] pt-[2rem] lg:mt-[4.5rem] lg:pt-0 flex flex-row items-center justify-center">
          <div
            style={{ direction: "ltr" }}
            className="w-fit">
            <PrimaryButton
              text={buttonText}
              onClick={() => {
                if (onOpenSlidingForm) {
                  onOpenSlidingForm();
                }
              }}
              size={isLg ? 64 : 52}
              type="accent"
              language={language}
              icon={true}
              isLPCtaButton={true}
            />
          </div>
        </div>
      </div>
    </section>
  );
}


export default LPDevicesSection;
